(() => {
  const ROOT_SELECTOR = '[data-collection-title-search]';
  const PRODUCT_SELECTOR = '[data-contentlm-replace] > .t4s-product';
  const LOAD_MORE_SELECTOR = '[data-wrap-lm] [data-load-more]:not([data-is-prev])';
  const DEFAULT_NEXT_SELECTOR = '.t4s-pagination__item.pagination__item--prev.pagination__item-arrow';
  const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const initialize = (root) => {
    if (root.dataset.collectionTitleSearchReady === 'true') return;
    root.dataset.collectionTitleSearchReady = 'true';
    const ajaxContainer = document.querySelector('[data-ntajax-container].t4s-main-collection-page');
    const section = ajaxContainer?.closest('.shopify-section') || document;
    const input = root.querySelector('[data-collection-title-search-input]');
    const form = root.querySelector('[data-collection-title-search-form]');
    const clearButton = root.querySelector('[data-collection-title-search-clear]');
    const status = root.querySelector('[data-collection-title-search-status]');
    const totalProducts = Number(root.dataset.totalProducts || 0);
    let mainSectionId = '';
    try { mainSectionId = JSON.parse(ajaxContainer?.dataset.ntajaxOptions || '{}').id || ''; }
    catch (error) { console.warn('[Octulos collection search] Invalid collection options.', error); }
    let loadPromise = null;
    let debounceTimer = null;
    let allProductsLoaded = false;
    const getProducts = () => Array.from(section.querySelectorAll(PRODUCT_SELECTOR));
    const getTitle = (product) => normalize(product.querySelector('.t4s-product-title')?.textContent || '');
    const getPagination = () => section.querySelector('.t4s-prs-footer');

    const getProductKey = (product) => {
      try {
        const options = JSON.parse(product.dataset.productOptions || '{}');
        if (options.id) return `id:${options.id}`;
      } catch (error) {
        console.warn('[Octulos collection search] Invalid product options.', error);
      }
      const link = product.querySelector('.t4s-product-title a');
      return `url:${link?.getAttribute('href') || ''}`;
    };

    const removeDuplicateProducts = () => {
      const seen = new Set();
      getProducts().forEach((product) => {
        const key = getProductKey(product);
        if (seen.has(key)) product.remove();
        else seen.add(key);
      });
    };

    const getEmptyMessage = () => {
      let empty = section.querySelector('[data-collection-title-search-empty]');
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'octulos-collection-search__empty';
        empty.dataset.collectionTitleSearchEmpty = '';
        empty.textContent = 'Geen kaarten gevonden met deze titel.';
        section.querySelector('[data-contentlm-replace]')?.insertAdjacentElement('afterend', empty);
      }
      return empty;
    };

    const applyFilter = () => {
      const query = normalize(input.value);
      let visibleCount = 0;
      getProducts().forEach((product) => {
        const matches = !query || getTitle(product).includes(query);
        product.hidden = !matches;
        if (matches) visibleCount += 1;
      });
      const pagination = getPagination();
      if (pagination) pagination.hidden = Boolean(query) || allProductsLoaded;
      const empty = getEmptyMessage();
      empty.hidden = !query || visibleCount > 0;
      clearButton.hidden = !query;
      if (!query) status.textContent = '';
      else if (loadPromise) status.textContent = `Alle ${totalProducts || ''} kaarten doorzoeken…`;
      else status.textContent = visibleCount === 1 ? '1 kaart gevonden' : `${visibleCount} kaarten gevonden`;
    };

    const waitForNextBatch = async (beforeCount, beforeHref) => {
      const timeoutAt = Date.now() + 15000;
      while (Date.now() < timeoutAt) {
        await sleep(150);
        const nextButton = section.querySelector(LOAD_MORE_SELECTOR);
        const productsAdded = getProducts().length > beforeCount;
        const pageAdvanced = !nextButton || nextButton.getAttribute('href') !== beforeHref;
        if (productsAdded && pageAdvanced) return;
      }
      throw new Error('Timed out while loading the next collection page.');
    };

    const loadAllProducts = async () => {
      let safetyCounter = 0;
      const loadedUrls = new Set();
      removeDuplicateProducts();
      while (safetyCounter < 20) {
        const button = section.querySelector(LOAD_MORE_SELECTOR);
        if (!button) {
          let nextLink = section.querySelector(DEFAULT_NEXT_SELECTOR);
          const grid = section.querySelector('[data-contentlm-replace]');
          while (nextLink && grid && safetyCounter < 20) {
            const nextUrl = new URL(nextLink.href, window.location.origin);
            const pageKey = nextUrl.toString();
            if (loadedUrls.has(pageKey)) break;
            loadedUrls.add(pageKey);
            if (mainSectionId) nextUrl.searchParams.set('section_id', mainSectionId);
            const response = await fetch(nextUrl.toString(), { credentials: 'same-origin' });
            if (!response.ok) throw new Error(`Collection page returned ${response.status}.`);
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextGrid = parsed.querySelector('[data-contentlm-replace]');
            if (!nextGrid) throw new Error('The next collection page did not contain a product grid.');
            Array.from(nextGrid.children).forEach((product) => {
              if (product.matches('.t4s-product')) grid.appendChild(document.importNode(product, true));
            });
            removeDuplicateProducts();
            nextLink = parsed.querySelector(DEFAULT_NEXT_SELECTOR);
            applyFilter();
            safetyCounter += 1;
          }
          break;
        }
        const beforeCount = getProducts().length;
        const beforeHref = button.getAttribute('href');
        if (!beforeHref || loadedUrls.has(beforeHref)) break;
        loadedUrls.add(beforeHref);
        button.click();
        await waitForNextBatch(beforeCount, beforeHref);
        removeDuplicateProducts();
        applyFilter();
        safetyCounter += 1;
      }
      allProductsLoaded = getProducts().length >= totalProducts;
    };

    const search = async () => {
      applyFilter();
      if (!normalize(input.value) || loadPromise) return;
      if (getProducts().length < totalProducts && (section.querySelector(LOAD_MORE_SELECTOR) || section.querySelector(DEFAULT_NEXT_SELECTOR))) {
        loadPromise = loadAllProducts();
        applyFilter();
        try { await loadPromise; }
        catch (error) {
          console.warn('[Octulos collection search]', error);
          status.textContent = 'Niet alle kaarten konden worden geladen. Probeer het opnieuw.';
        } finally {
          loadPromise = null;
          applyFilter();
        }
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(search, 220);
    });
    input.addEventListener('search', search);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      search();
    });
    clearButton.addEventListener('click', () => {
      input.value = '';
      input.focus();
      search();
    });
  };

  const initializeAll = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  new MutationObserver(initializeAll).observe(document.documentElement, { childList: true, subtree: true });
})();
